import { useMemo } from "react";
import { usePerformanceReportQuery } from "../../App/Queries/ReportingQueries";
import { getPreviousTimespan } from "../CandidatesAddedByCampaign/State/CandidatesAddedByCampaignReportState";
import _ from "lodash";
import { MessageDeliveryType } from "../../App/Enums/Message";
import { IPerformanceReport } from "../../App/Typings/Reports/CampaignEmailPerformanceReport";

export type TextPerformanceReportState = ReturnType<typeof useTextPerformanceReportState>;

/**
 * Gets the Group text performance report data based on the provided parameters
 * @param startDate The start date of the report
 * @param endDate The end date of the report
 * @param campaignId The campaign id to filter the report by
 * @param enablePreviousReport Whether or not to enable the previous report comparison query
 * @returns
 */
export const useTextPerformanceReportState = (startDate: Date, endDate: Date, groupId?: string) => {
  const numDaysInReport = useMemo(() => {
    return Math.round((endDate.valueOf() - startDate.valueOf()) / (1000 * 60 * 60 * 24));
  }, [endDate, startDate]);

  const { data: reportData, isLoading } = usePerformanceReportQuery(
    startDate,
    endDate,
    MessageDeliveryType.Text,
    groupId,
  );

  const {
    prevStartDate,
    prevEndDate,
    prevDeliveredCount,
    prevBounceCount,
    prevReplyCount,
    prevUnsubscribeCount,
    isLoadingPrevReport,
    deliveredPercentChange,
    bouncePercentChange,
    replyPercentChange,
    unsubscribePercentChange,
  } = usePreviousReportData(startDate, endDate, groupId, reportData);

  return {
    getters: {
      isLoading,
      isLoadingPrevReport,
      numDaysInReport,
      reportData,
      prevStartDate,
      prevEndDate,
      prevDeliveredCount,
      prevBounceCount,
      prevReplyCount,
      prevUnsubscribeCount,
      deliveredPercentChange,
      bouncePercentChange,
      replyPercentChange,
      unsubscribePercentChange,
    },
  } as const;
};

const usePreviousReportData = (
  startDate: Date,
  endDate: Date,
  groupId?: string,
  reportData?: IPerformanceReport,
) => {
  const { prevStartDate, prevEndDate } = getPreviousTimespan(startDate, endDate);
  const { data: prevReportData, isLoading: isLoadingPrevReport } = usePerformanceReportQuery(
    prevStartDate,
    prevEndDate,
    MessageDeliveryType.Text,
    groupId,
    reportData !== undefined,
  );

  const prevDeliveredCount =
    prevReportData?.dataPoints?.reduce((acc, curr) => acc + curr.deliveredCount, 0) ?? 0;
  const deliveredPercentChange = getPercentChange("deliveredCount", prevReportData, reportData);
  const bouncePercentChange = getPercentChange("bounceCount", prevReportData, reportData);
  const prevBounceCount = prevReportData?.dataPoints?.reduce((acc, curr) => acc + curr.bounceCount, 0) ?? 0;
  const replyPercentChange = getPercentChange("replyCount", prevReportData, reportData);
  const prevReplyCount = prevReportData?.dataPoints?.reduce((acc, curr) => acc + curr.replyCount, 0) ?? 0;
  const unsubscribePercentChange = getPercentChange("unsubscribeCount", prevReportData, reportData);
  const prevUnsubscribeCount =
    prevReportData?.dataPoints?.reduce((acc, curr) => acc + curr.unsubscribeCount, 0) ?? 0;

  return {
    prevStartDate,
    prevEndDate,
    prevDeliveredCount,
    prevBounceCount,
    prevReplyCount,
    prevUnsubscribeCount,
    isLoadingPrevReport,
    deliveredPercentChange,
    bouncePercentChange,
    replyPercentChange,
    unsubscribePercentChange,
  };
};

export function getPercentChange(dataKey: string, prev?: IPerformanceReport, current?: IPerformanceReport) {
  if (!prev || !current) {
    return 0;
  }
  const previousCount = prev.dataPoints?.reduce((acc, curr) => acc + curr[dataKey], 0) ?? 0;
  const currentCount = current.dataPoints?.reduce((acc, curr) => acc + curr[dataKey], 0) ?? 0;
  const percentChange = !previousCount
    ? currentCount * 100
    : ((currentCount - previousCount) / previousCount) * 100;
  return _.round(percentChange, 2);
}
